// src/pages/ResetPassword.tsx
import { useState } from 'react'
import { supabase } from '../services/supabase'
import { useAuth } from '../stores/useAuth'

export default function ResetPasswordPage() {
  const completePasswordRecovery = useAuth((s) => s.completePasswordRecovery)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!password || !confirmPassword) { setError('Please fill in both fields'); return }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return }
    if (password !== confirmPassword) { setError('Passwords do not match'); return }

    setError(null); setLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) {
        setError(error.message)
      } else {
        setDone(true)
        // Clear recovery mode so App.tsx naturally falls through to the
        // normal dashboard now that the password has actually been changed.
        completePasswordRecovery()
      }
    } catch {
      setError('Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page bg-base" style={{ minHeight: '100dvh' }}>
      <div className="auth-box">
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--xl)' }}>
          <div className="logo-mark">L</div>
          <h1 className="display-md text-primary" style={{ marginBottom: 'var(--xs)' }}>Loqin</h1>
          <p className="body text-muted" style={{ textAlign: 'center' }}>A focus operating system for founders</p>
        </div>

        {done ? (
          <div className="animate-fade-slide-up" style={{ textAlign: 'center', padding: 'var(--xl) 0' }}>
            <div style={{ fontSize: 40, marginBottom: 'var(--md)' }}>✅</div>
            <p className="title text-signal" style={{ marginBottom: 'var(--sm)' }}>Password updated</p>
            <p className="body text-muted">Taking you to your dashboard…</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 'var(--lg)' }}>
              <h2 className="title text-primary">Set a new password</h2>
              <p className="body-md text-muted" style={{ marginTop: 'var(--xs)' }}>
                Choose a new password for your account.
              </p>
            </div>

            {/* New password */}
            <div className="input-wrap">
              <label className="input-label" htmlFor="password">New password</label>
              <input
                id="password"
                className="input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              />
            </div>

            {/* Confirm password */}
            <div className="input-wrap">
              <label className="input-label" htmlFor="confirmPassword">Confirm password</label>
              <input
                id="confirmPassword"
                className="input"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              />
            </div>

            {error && <p className="body-md" style={{ color: 'var(--noise)', marginBottom: 'var(--md)' }}>{error}</p>}

            <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
              {loading ? <span className="spinner" /> : null}
              {loading ? 'Please wait…' : 'Update Password'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
